// ConversationTimelineRail — desktop-web-only vertical rail hugging the right
// edge of the chat view, one tick mark per user prompt in the conversation
// (mirrors ChatGPT's conversation timeline). Hovering the rail expands it into
// a list of the prompts; clicking one scrolls the conversation to that turn
// via the existing requestScrollToMessage pipeline (wired by the parent).
//
// The parent (ChatView) gates rendering: web platform, not XS, not compact,
// not a Work thread, and at least two prompts. It also passes the composer
// height as bottomInset so the expanded list cannot open behind the input.
// This component only handles presentation.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';

// Collapsed rail shows at most this many ticks; longer conversations are
// sampled evenly so the rail never outgrows the viewport.
const MAX_TICKS = 24;
const PANEL_WIDTH = 288;
const PANEL_MAX_HEIGHT = 440;
// Floor for the expanded panel when something above it (the Sources / Outputs
// card) is eating into the column; below this it scrolls instead of shrinking.
const PANEL_MIN_HEIGHT = 200;
const PANEL_BOTTOM_GUTTER = 12;
// Breathing room between the panel and whatever sits above it.
const PANEL_TOP_GAP = 8;
// Rows are fixed-height (single line) so the expanded panel can scroll the
// active prompt into view with simple offset math.
const ROW_HEIGHT = 34;

const PromptRow = React.memo(function PromptRow({ prompt, isActive, onPress }) {
  const { colors } = useTheme();
  const [hovered, setHovered] = useState(false);
  const handlePress = useCallback(() => {
    onPress(prompt.id);
  }, [onPress, prompt.id]);
  return (
    <Pressable
      onPress={handlePress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[
        styles.row,
        { backgroundColor: hovered ? colors.solid_color_tertiary : 'transparent' },
        Platform.OS === 'web' && { cursor: 'pointer' },
      ]}
    >
      <View
        style={[
          styles.rowMarker,
          { backgroundColor: isActive ? colors.text_color_primary : colors.border_color_secondary },
        ]}
      />
      <Text
        size="s"
        weight={isActive ? 'bold' : 'normal'}
        color={isActive ? 'primary' : 'secondary'}
        numberOfLines={1}
        style={{ flex: 1 }}
      >
        {prompt.text}
      </Text>
    </Pressable>
  );
});

PromptRow.propTypes = {
  prompt: PropTypes.shape({
    id: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  }).isRequired,
  isActive: PropTypes.bool,
  onPress: PropTypes.func.isRequired,
};

const ConversationTimelineRail = ({
  prompts,
  activePromptId,
  onPromptPress,
  topInset = 0,
  bottomInset = 0,
}) => {
  const { colors } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const scrollRef = useRef(null);
  // The rail's onLayout box is the column minus the composer (bottomInset is
  // applied as `bottom` on the root). The hover zone's offset converts
  // column-space insets into the panel's coordinate space (the panel must
  // stay a child of the hover zone).
  const [railHeight, setRailHeight] = useState(0);
  const [hoverZoneTop, setHoverZoneTop] = useState(0);

  const handleRailLayout = useCallback((event) => {
    const height = event?.nativeEvent?.layout?.height ?? 0;
    setRailHeight((prev) => (Math.abs(prev - height) < 1 ? prev : height));
  }, []);
  const handleHoverZoneLayout = useCallback((event) => {
    const y = event?.nativeEvent?.layout?.y ?? 0;
    setHoverZoneTop((prev) => (Math.abs(prev - y) < 1 ? prev : y));
  }, []);

  // With the Sources card occupying the top of the gutter, hang the panel just
  // below it rather than centering it (which would put the two on top of each
  // other on tall conversations). Always clamp to the measured column so a
  // long prompt list cannot open behind the floating composer.
  const isAnchoredBelowInset = topInset > 0 && railHeight > 0;
  const panelMaxHeight = useMemo(() => {
    if (!railHeight) return PANEL_MAX_HEIGHT;
    const topReserve = isAnchoredBelowInset ? topInset + PANEL_TOP_GAP : 0;
    const usable = railHeight - topReserve - PANEL_BOTTOM_GUTTER;
    return Math.max(PANEL_MIN_HEIGHT, Math.min(PANEL_MAX_HEIGHT, usable));
  }, [isAnchoredBelowInset, railHeight, topInset]);
  const panelPositionStyle = useMemo(() => {
    if (!isAnchoredBelowInset) return styles.panelCentered;
    // Never let the panel run off the bottom; in a viewport too short for both
    // it slides back up rather than being cut off.
    const lowestTop = Math.max(0, railHeight - PANEL_BOTTOM_GUTTER - panelMaxHeight);
    const top = Math.min(topInset + PANEL_TOP_GAP, lowestTop);
    return { top: top - hoverZoneTop };
  }, [isAnchoredBelowInset, railHeight, topInset, hoverZoneTop, panelMaxHeight]);

  // Evenly sample ticks (always keeping first and last) when the conversation
  // has more prompts than the collapsed rail can show.
  const tickPrompts = useMemo(() => {
    if (prompts.length <= MAX_TICKS) return prompts;
    const sampled = [];
    let lastIdx = -1;
    for (let i = 0; i < MAX_TICKS; i += 1) {
      const idx = Math.round((i * (prompts.length - 1)) / (MAX_TICKS - 1));
      if (idx === lastIdx) continue;
      lastIdx = idx;
      sampled.push(prompts[idx]);
    }
    return sampled;
  }, [prompts]);

  // When ticks are sampled, highlight the tick nearest the active prompt.
  const activeTickId = useMemo(() => {
    if (!activePromptId) return null;
    if (tickPrompts.length === prompts.length) return activePromptId;
    const activeIdx = prompts.findIndex(p => p.id === activePromptId);
    if (activeIdx < 0) return null;
    let bestId = null;
    let bestDist = Infinity;
    for (const tick of tickPrompts) {
      const idx = prompts.indexOf(tick);
      const dist = Math.abs(idx - activeIdx);
      if (dist < bestDist) {
        bestDist = dist;
        bestId = tick.id;
      }
    }
    return bestId;
  }, [activePromptId, prompts, tickPrompts]);

  // Center the active prompt in the panel when it opens.
  useEffect(() => {
    if (!expanded || !scrollRef.current) return;
    const idx = prompts.findIndex(p => p.id === activePromptId);
    if (idx < 0) return;
    const y = Math.max(0, idx * ROW_HEIGHT - (panelMaxHeight - ROW_HEIGHT) / 2);
    scrollRef.current.scrollTo({ y, animated: false });
    // Re-centering on every active-id change while open would fight the
    // user's own panel scrolling, so only run on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  // Collapsing is debounced: pointer-leave starts a short grace timer and
  // re-entering (ticks, gap, or panel — they share one hover zone) cancels it.
  // Without this, the brief hover-out the browser fires while layout changes
  // under the cursor would immediately close the panel and make hovering feel
  // unreliable.
  const collapseTimerRef = useRef(null);
  const cancelCollapse = useCallback(() => {
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }
  }, []);
  const handleHoverIn = useCallback(() => {
    cancelCollapse();
    setExpanded(true);
  }, [cancelCollapse]);
  const handleHoverOut = useCallback(() => {
    cancelCollapse();
    collapseTimerRef.current = setTimeout(() => setExpanded(false), 250);
  }, [cancelCollapse]);
  useEffect(() => () => cancelCollapse(), [cancelCollapse]);

  return (
    <View
      style={[styles.root, bottomInset > 0 && { bottom: bottomInset }]}
      pointerEvents="box-none"
      onLayout={handleRailLayout}
    >
      {/* Plain mouse-enter/leave (not Pressable's onHoverIn/Out): React's
          synthetic enter/leave correctly treats the absolutely-positioned
          panel as "inside", whereas RNW's pointer-based hover fires a leave
          when the pointer crosses onto the overflowing panel — which is what
          made the panel vanish mid-hover. */}
      <View
        onMouseEnter={handleHoverIn}
        onMouseLeave={handleHoverOut}
        onLayout={handleHoverZoneLayout}
        style={styles.hoverZone}
      >
        {expanded && (
          <View
            onMouseEnter={handleHoverIn}
            onMouseLeave={handleHoverOut}
            style={[
              styles.panel,
              panelPositionStyle,
              {
                backgroundColor: colors.solid_color_primary,
                borderColor: colors.border_color_secondary,
              },
            ]}
          >
            <ScrollView
              ref={scrollRef}
              style={[styles.panelScroll, { maxHeight: panelMaxHeight }]}
              contentContainerStyle={styles.panelContent}
              showsVerticalScrollIndicator={false}
            >
              {prompts.map(prompt => (
                <PromptRow
                  key={prompt.id}
                  prompt={prompt}
                  isActive={prompt.id === activePromptId}
                  onPress={onPromptPress}
                />
              ))}
            </ScrollView>
          </View>
        )}
        {/* Ticks stay mounted while the panel is open (just faded out) so the
            element under the cursor never unmounts mid-hover. */}
        <View
          style={[styles.ticksColumn, expanded && { opacity: 0 }]}
          pointerEvents="none"
        >
          {tickPrompts.map(prompt => {
            const isActive = prompt.id === activeTickId;
            return (
              <View
                key={prompt.id}
                style={[
                  styles.tick,
                  isActive && styles.tickActive,
                  {
                    backgroundColor: isActive
                      ? colors.text_color_primary
                      : colors.text_color_tertiary,
                    opacity: isActive ? 1 : 0.45,
                  },
                ]}
              />
            );
          })}
        </View>
      </View>
    </View>
  );
};

ConversationTimelineRail.propTypes = {
  prompts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  })).isRequired,
  activePromptId: PropTypes.string,
  onPromptPress: PropTypes.func.isRequired,
  // Height already claimed at the top of the column (Sources / Outputs card).
  topInset: PropTypes.number,
  // Height already claimed at the bottom of the column (floating composer).
  bottomInset: PropTypes.number,
};

const styles = StyleSheet.create({
  root: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    // Sit just inside the list's scrollbar so it stays clickable.
    right: 14,
    justifyContent: 'center',
    alignItems: 'flex-end',
    zIndex: 20,
  },
  hoverZone: {
    position: 'relative',
    alignItems: 'flex-end',
    justifyContent: 'center',
    // Generous padding = forgiving hover target around the slim ticks.
    paddingVertical: 16,
    paddingLeft: 20,
    paddingRight: 4,
  },
  ticksColumn: {
    alignItems: 'flex-end',
    gap: 6,
    paddingRight: 2,
  },
  tick: {
    width: 12,
    height: 2,
    borderRadius: 1,
  },
  tickActive: {
    width: 18,
  },
  panel: {
    // Overlaid on the (faded) ticks rather than replacing them in the layout,
    // so opening the panel never resizes the hover zone under the pointer.
    position: 'absolute',
    right: 4,
    width: PANEL_WIDTH,
    borderRadius: 12,
    borderWidth: 1,
    overflow: 'hidden',
    zIndex: 1,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 8px 30px rgba(0, 0, 0, 0.16)' }
      : {}),
  },
  panelCentered: {
    top: '50%',
    transform: [{ translateY: '-50%' }],
  },
  panelScroll: {
    ...(Platform.OS === 'web'
      ? { overflowY: 'auto', overflowX: 'hidden' }
      : {}),
  },
  panelContent: {
    paddingVertical: 6,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    height: ROW_HEIGHT,
    paddingHorizontal: 10,
    marginHorizontal: 4,
    borderRadius: 8,
  },
  rowMarker: {
    width: 10,
    height: 2,
    borderRadius: 1,
  },
});

export default React.memo(ConversationTimelineRail);
